import type { Author } from "../../data/authors";
import { site } from "../../data/site";
import type { Post } from "../../lib/posts";

export function cleanSchema(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => cleanSchema(item)).filter((item) => item !== undefined && item !== null);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entry]) => entry !== undefined && entry !== null && entry !== "")
        .map(([key, entry]) => [key, cleanSchema(entry)])
    );
  }

  return value;
}

export function serializeJsonLd(data: unknown) {
  return JSON.stringify(cleanSchema(data), null, 2).replace(/</g, "\\u003c");
}

export function absoluteUrl(pathOrUrl: string) {
  if (pathOrUrl.startsWith("http")) return pathOrUrl;
  return `${site.url}${pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`}`;
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: site.name,
    url: site.url,
    description: site.description,
    inLanguage: site.language,
    potentialAction: {
      "@type": "SearchAction",
      target: `${site.url}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };
}

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: site.publisher.name,
    url: site.url,
    logo: absoluteUrl(site.publisher.logo),
    sameAs: Object.values(site.socials)
  };
}

export function personSchema(author: Author) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: author.name,
    url: absoluteUrl(`/author/${author.slug}`),
    image: absoluteUrl(author.avatar),
    jobTitle: author.role,
    description: author.bio,
    sameAs: author.sameAs
  };
}

export function articleSchema(post: Post) {
  return {
    "@context": "https://schema.org",
    "@type": ["Article", "BlogPosting"],
    headline: post.title,
    description: post.excerpt,
    image: absoluteUrl(post.featuredImage),
    datePublished: post.publishedAt,
    dateModified: post.updatedAt,
    author: {
      "@type": "Person",
      name: post.authorProfile.name,
      url: absoluteUrl(post.authorUrl),
      image: absoluteUrl(post.authorProfile.avatar),
      sameAs: post.authorProfile.sameAs
    },
    publisher: {
      "@type": "Organization",
      name: site.publisher.name,
      logo: {
        "@type": "ImageObject",
        url: absoluteUrl(site.publisher.logo)
      }
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": post.absoluteUrl
    },
    url: post.absoluteUrl,
    keywords: post.tags.join(", "),
    articleSection: post.category,
    inLanguage: site.language
  };
}

export function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.url)
    }))
  };
}

export function collectionPageSchema(name: string, description: string, url: string, itemUrls: string[]) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    description,
    url: absoluteUrl(url),
    isPartOf: {
      "@type": "WebSite",
      name: site.name,
      url: site.url
    },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: itemUrls.map((itemUrl, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: absoluteUrl(itemUrl)
      }))
    }
  };
}

export function faqPageSchema(post: Post) {
  const faqPattern = /<summary>\s*([\s\S]*?)\s*<\/summary>\s*<p>\s*([\s\S]*?)\s*<\/p>/g;
  let match;
  const mainEntity = [];

  while ((match = faqPattern.exec(post.body)) !== null) {
    mainEntity.push({
      "@type": "Question",
      name: match[1].replace(/[*_~`]/g, "").trim(),
      acceptedAnswer: {
        "@type": "Answer",
        text: match[2].replace(/<[^>]+>/g, "").trim()
      }
    });
  }

  if (mainEntity.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity
  };
}
